import { useState } from 'react';
import { Eye, EyeOff, CheckCircle, XCircle, ExternalLink, Shield, AlertTriangle } from 'lucide-react';
import { AIProvider } from '../types';
import { ModelSelector } from './ModelSelector';

interface APIKeyManagerProps {
  selectedProvider: AIProvider;
  selectedModel: string;
  apiKeys: Record<AIProvider, string>;
  onProviderChange: (provider: AIProvider) => void;
  onModelChange: (model: string) => void;
  onApiKeyChange: (provider: AIProvider, apiKey: string) => void;
  onValidateKey: (provider: AIProvider, apiKey: string) => Promise<boolean>;
  validationStatus: Record<AIProvider, 'idle' | 'validating' | 'valid' | 'invalid'>;
}

const PROVIDER_INFO = {
  openai: {
    name: 'OpenAI',
    description: 'GPT-4 and GPT-3.5 models',
    placeholder: 'sk-...',
    docsUrl: 'https://platform.openai.com/api-keys',
    color: 'green'
  },
  gemini: {
    name: 'Google Gemini',
    description: 'Gemini Pro and Ultra models',
    placeholder: 'AIza...',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    color: 'blue'
  },
  openrouter: {
    name: 'OpenRouter',
    description: 'Access to multiple AI models',
    placeholder: 'sk-or-...',
    docsUrl: 'https://openrouter.ai/keys',
    color: 'purple'
  }
} as const;

export function APIKeyManager({
  selectedProvider,
  selectedModel,
  apiKeys,
  onProviderChange,
  onModelChange,
  onApiKeyChange,
  onValidateKey,
  validationStatus
}: APIKeyManagerProps) {
  const [showKeys, setShowKeys] = useState<Record<AIProvider, boolean>>({
    openai: false,
    gemini: false,
    openrouter: false
  });

  const toggleKeyVisibility = (provider: AIProvider) => {
    setShowKeys(prev => ({ ...prev, [provider]: !prev[provider] }));
  };

  const handleKeyChange = (provider: AIProvider, value: string) => {
    onApiKeyChange(provider, value);
  };

  const handleValidateKey = async (provider: AIProvider) => {
    const apiKey = apiKeys[provider];
    if (apiKey.trim()) {
      await onValidateKey(provider, apiKey);
    }
  };

  const getValidationIcon = (provider: AIProvider) => {
    const status = validationStatus[provider];
    switch (status) {
      case 'validating':
        return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>;
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'invalid':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getProviderColorClasses = (provider: AIProvider, selected: boolean) => {
    const colors = PROVIDER_INFO[provider].color;
    if (selected) {
      switch (colors) {
        case 'green':
          return 'border-green-500 bg-green-50';
        case 'blue':
          return 'border-blue-500 bg-blue-50';
        case 'purple':
          return 'border-purple-500 bg-purple-50';
      }
    }
    return 'border-gray-200 bg-white hover:border-gray-300';
  };

  return (
    <div className="space-y-6">
      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-1">Secure API Key Handling</h4>
            <p className="text-sm text-blue-800">
              Your API keys are used securely for this session only and are never stored on our servers. 
              All communication is encrypted and keys are cleared when you close the browser.
            </p>
          </div>
        </div>
      </div>

      {/* Provider Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select AI Provider</h3>
        <div className="grid gap-4">
          {(Object.keys(PROVIDER_INFO) as AIProvider[]).map((provider) => {
            const info = PROVIDER_INFO[provider];
            const isSelected = selectedProvider === provider;
            const hasKey = apiKeys[provider].trim().length > 0;
            // Check if provider is valid for styling

            return (
              <div
                key={provider}
                className={`cursor-pointer rounded-lg border-2 p-4 transition-all duration-200 ${getProviderColorClasses(provider, isSelected)}`}
                onClick={() => onProviderChange(provider)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      isSelected 
                        ? `border-${info.color}-500 bg-${info.color}-500` 
                        : 'border-gray-300'
                    }`}>
                      {isSelected && <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{info.name}</h4>
                      <p className="text-sm text-gray-600">{info.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {hasKey && getValidationIcon(provider)}
                    <a
                      href={info.docsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>

                {/* API Key Input */}
                {isSelected && (
                  <div className="mt-4 space-y-3">
                    <div className="relative">
                      <input
                        type={showKeys[provider] ? 'text' : 'password'}
                        placeholder={`Enter your ${info.name} API key (${info.placeholder})`}
                        value={apiKeys[provider]}
                        onChange={(e) => handleKeyChange(provider, e.target.value)}
                        className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                        {getValidationIcon(provider)}
                        <button
                          type="button"
                          onClick={() => toggleKeyVisibility(provider)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showKeys[provider] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Validation Button */}
                    <button
                      onClick={() => handleValidateKey(provider)}
                      disabled={!apiKeys[provider].trim() || validationStatus[provider] === 'validating'}
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      {validationStatus[provider] === 'validating' ? 'Validating...' : 'Validate API Key'}
                    </button>

                    {/* Validation Status */}
                    {validationStatus[provider] === 'valid' && (
                      <div className="flex items-center space-x-2 text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        <span>API key is valid and ready to use</span>
                      </div>
                    )}
                    {validationStatus[provider] === 'invalid' && (
                      <div className="flex items-center space-x-2 text-red-600 text-sm">
                        <XCircle className="h-4 w-4" />
                        <span>Invalid API key. Please check and try again.</span>
                      </div>
                    )}

                    {/* Model Selection */}
                    {validationStatus[provider] === 'valid' && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <ModelSelector
                          provider={provider}
                          selectedModel={selectedModel}
                          onModelChange={onModelChange}
                          disabled={false}
                          apiKey={apiKeys[provider]}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Warning for no valid key */}
      {!Object.values(validationStatus).includes('valid') && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-amber-900 mb-1">API Key Required</h4>
              <p className="text-sm text-amber-800">
                Please provide and validate an API key from one of the supported providers to continue with Auto Mode.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
